import { Spin, Tag } from 'antd';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Button } from '../../../custom';
import {
  useGetScholarshipPercentageConfigQuery,
} from '../../../hooks/api/scholarship/useGetScholarshipPercentageConfigQuery';
import {
  ScholarshipPercentagePayload,
  useConfigureScholarshipPercentageMutation,
} from '../../../hooks/api/scholarship/useConfigureScholarshipPercentageMutation';

import styles from '../../setup/schoolConfig/schoolConfig.module.scss';

const validationSchema = Yup.object().shape({
  minPercent: Yup.number()
    .min(0, 'Must be 0 or greater')
    .max(100, 'Must be 100 or less')
    .required('Min percent is required'),
  maxPercent: Yup.number()
    .min(0, 'Must be 0 or greater')
    .max(100, 'Must be 100 or less')
    .required('Max percent is required'),
  stepInterval: Yup.number()
    .min(1, 'Must be at least 1')
    .required('Step interval is required'),
  allowedValues: Yup.string(),
});

const NumberField = ({ label, name }: { label: string; name: keyof ScholarshipPercentagePayload }) => (
  <Field name={name}>
    {({ field, meta }: FieldProps) => (
      <div className={styles.field}>
        <label>{label}</label>
        <div className={styles.control}>
          <input
            {...field}
            type="number"
            className={styles.numberInput}
            value={field.value ?? ''}
          />
          {meta.touched && meta.error && <div className={styles.error}>{meta.error}</div>}
        </div>
      </div>
    )}
  </Field>
);

const TextField = ({ label, name, placeholder }: { label: string; name: keyof ScholarshipPercentagePayload; placeholder?: string }) => (
  <Field name={name}>
    {({ field, meta }: FieldProps) => (
      <div className={styles.field}>
        <label>{label}</label>
        <div className={styles.control}>
          <input
            {...field}
            type="text"
            className={styles.numberInput}
            placeholder={placeholder}
            value={field.value ?? ''}
          />
          {meta.touched && meta.error && <div className={styles.error}>{meta.error}</div>}
        </div>
      </div>
    )}
  </Field>
);

const ScholarshipPercentageConfig = () => {
  const { percentageConfigData, isLoading, isError, errorMessage } = useGetScholarshipPercentageConfigQuery();
  const { configureHandler, isLoading: isSaving } = useConfigureScholarshipPercentageMutation();

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>Error: {errorMessage}</div>;
  }

  const initialValues: ScholarshipPercentagePayload = {
    minPercent: percentageConfigData?.minPercent ?? 0,
    maxPercent: percentageConfigData?.maxPercent ?? 100,
    stepInterval: percentageConfigData?.stepInterval ?? 5,
    allowedValues: percentageConfigData?.allowedValues ?? '',
  };

  return (
    <main>
      <section className="space-between">
        <h3>Scholarship Percentage Config</h3>
      </section>

      <section className={styles.formCard}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => configureHandler(values, resetForm)}
          enableReinitialize
        >
          <Form>
            <div className={styles.section}>
              <h4>Percentage Range</h4>
              <div className={styles.grid}>
                <NumberField label="Min Percent (%)" name="minPercent" />
                <NumberField label="Max Percent (%)" name="maxPercent" />
                <NumberField label="Step Interval" name="stepInterval" />
                <TextField
                  label="Allowed Values"
                  name="allowedValues"
                  placeholder="e.g. 10,25,50,75,100 (optional override)"
                />
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                type="submit"
                disabled={isSaving}
                isLoading={isSaving}
                text={isSaving ? 'Saving...' : 'Save Configuration'}
              />
            </div>
          </Form>
        </Formik>
      </section>

      {percentageConfigData?.generatedOptions && percentageConfigData.generatedOptions.length > 0 && (
        <section className={styles.formCard}>
          <div className={styles.section}>
            <h4>Generated Options</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {percentageConfigData.generatedOptions.map((val) => (
                <Tag key={val} color="blue">{val}%</Tag>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default ScholarshipPercentageConfig;
